/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import { STATES } from "../constants/GeneralConstants";
import fetchHelper from "../services/fetchHelper";
import MimeTypes from "../constants/MimeTypes";
import dayjs from "dayjs";
import { sanitize } from "sanitize-filename-ts";
import { dateFormats } from "../constants/DateFormatConstants";

export const cpt30Pro = (deviceType, thirthyCycleEnd) => {
  let dt = deviceType.toLowerCase();
  if (dt !== "ilr" && dt !== "other" && thirthyCycleEnd) return 93297;
  if (dt === "ilr" && thirthyCycleEnd) return 93298;
  if (dt !== "other" && thirthyCycleEnd) return 93299;
  return "-";
};
export const cpt90Pro = (deviceType, nintyCycleEnd, thirthyCycleEnd) => {
  let dt = deviceType.toLowerCase();
  if ((dt === "pacemaker" || dt === "crtp") && nintyCycleEnd) return 93294;
  if ((dt === "icd" || dt === "crtd") && nintyCycleEnd) return 93295;
  if (dt !== "ilr" && dt !== "other" && nintyCycleEnd && !thirthyCycleEnd) return 93296;
  return "-";
};
export const d8 = (d) => {
  if (d) {
    const b = new Date(d);
    return [
      b.getFullYear().toString(),
      ("0" + (b.getMonth() + 1)).slice(-2),
      ("0" + b.getDate()).slice(-2),
    ].join("-");
  }
  return "";
};

export const deviceType = (type) => {
  switch (type) {
    case "PACEMAKER":
      return "PPM";
    default:
      return type;
  }
};

export const formatFileName = (fileURL) => fileURL?.replace("C:\\fakepath\\", "").split("/").pop();

export const getStateAbbreviation = (state) => {
  if (!state) return "";
  else if (state in STATES) {
    return state;
  } else {
    const selectedState = Object.keys(STATES).find(
      (item) => STATES[item].toLowerCase() === state.toLowerCase()
    );

    return selectedState || state;
  }
};

export const downloadAsBlob = async (url, fileName, mimeType = MimeTypes.PDF) => {
  const blob = await fetchHelper.getFile({ url: url, headers: { "Content-Type": mimeType } });

  // Create blob link to download
  const href = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", fileName);
  link.style.display = "none";

  // Append to html link element page
  document.body.appendChild(link);

  // Start download
  link.click();

  // Clean up and remove the link
  link.parentNode.removeChild(link);
};

// Converts date to ISO format yyyy-mm-dd
export const toISODate = (date) => {
  if (date) {
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const realDate = new Date(date.getTime() - userTimezoneOffset);
    return realDate.toISOString().substring(0, 10);
  }
  return "";
};

//Converts date from UTC string to Local Date
export const convertUTCDateToLocalDate = (UTCDateString, selectedFormat) => {
  let convertdLocalTime = new Date(UTCDateString);
  const hourOffset = convertdLocalTime.getTimezoneOffset() / 60;
  convertdLocalTime.setHours(convertdLocalTime.getHours() + hourOffset);
  convertdLocalTime = dayjs(convertdLocalTime).format(dateFormats[selectedFormat]);
  return convertdLocalTime;
};

export const fromUTCtoUserLocalDateTime = (date, selectedFormat) => {
  let newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
  const offset = date.getTimezoneOffset() / 60;
  const hours = date.getHours();
  newDate.setHours(hours - offset);
  newDate = dayjs(newDate).format(dateFormats[selectedFormat]);
  return newDate;
};

export const formatDate = (date, selectedFormat) => dayjs(date).format(dateFormats[selectedFormat]);

export const getRandomNumber = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const dateFormatForArrays = (date) => {
  if (typeof date === "string") date = date.split("-");
  let mm = date[1].toString();
  let dd = date[2].toString();
  if (mm.charAt(0) == "0") date[1] = mm.replace("0", "").split("");
  if (dd.charAt(0) == "0") date[2] = dd.replace("0", "").split("");
  let newDate = [date[1], date[2], date[0]];
  return newDate.join("/");
};

/**
 * @param patient
 * @param {number} patient.dob
 * @param {string} patient.name
 * @param {string} patient.family
 * @param {string} id
 * @return {string|number}
 */
export const getSafeIrFilename = (patient, id) => {
  const { name, family, dob } = patient;

  let dateOfBirth = "";
  if (dob) {
    dateOfBirth = `${dayjs(dob).format("MM-DD-YYYY")}`;
  }

  const formattedFilename = [family, name, dateOfBirth, id.toString()]
    .filter(Boolean)
    .map((filenamePiece) => filenamePiece.trim().replace(/\s+/g, "_"))
    .join("_");

  return sanitize(formattedFilename, {
    replacement: "",
  });
};

export const handleErrors = (addToast, err) => {
  const errors = err instanceof Object ? err : JSON.parse(err);
  const errArray = errors.validationErrors || errors.errors;
  errArray.forEach((err) => {
    addToast(err.msg || err.errorMessage, {
      appearance: "error",
      autoDismiss: false,
    });
  });
};
